import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  User, 
  UserPlus,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { LoginFormData, User as UserType } from '../../types';
import toast from 'react-hot-toast';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: ''
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Use the new AuthContext login with API
      await login({
          email: formData.email,
        password: formData.password
      });
      
      // Navigation will be handled by the AuthContext after successful login
      navigate('/admin'); // Default redirect, will be updated based on user role
    } catch (error) {
      toast.error('Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const demoCredentials = [
    { type: 'Patient', email: 'patient@demo.com', password: 'demo123' },
    { type: 'Admin', email: 'admin@demo.com', password: 'admin123' },
    { type: 'Doctor', email: 'doctor@demo.com', password: 'doctor123' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 flex items-center justify-center p-4">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-500 rounded-full opacity-10 blur-3xl"></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-4">
            <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-2xl">
              <span className="text-white font-bold text-2xl">C</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">ClinicIT</h1>
              <p className="text-slate-300 text-sm">Medical Center</p>
            </div>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-2">
              {isRegistering ? 'Create Account' : 'Sign In'}
            </h2>
            <p className="text-slate-600">
              {isRegistering ? 'Join ClinicIT today' : 'Access your medical portal'}
            </p>
      </div>

                {/* Toggle Buttons */}
          <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
                  <button
                    onClick={() => setIsRegistering(false)}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      !isRegistering 
                  ? 'bg-white text-slate-800 shadow-md'
                  : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
              <User className="w-4 h-4 mr-2" />
                    Sign In
                  </button>
                  <button
                    onClick={() => setIsRegistering(true)}
              className={`flex-1 flex items-center justify-center px-4 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      isRegistering 
                  ? 'bg-white text-slate-800 shadow-md'
                  : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
              <UserPlus className="w-4 h-4 mr-2" />
                    Sign Up
                  </button>
                </div>

                {/* Login Form */}
                {!isRegistering ? (
                  <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Email</label>
                <div className="relative">
                  <Mail className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      required
                    className="w-full pl-12 pr-4 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your email"
                    />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">Password</label>
                    <div className="relative">
                  <Lock className="w-5 h-5 absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400" />
                  <input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        required
                    className="w-full pl-12 pr-12 py-4 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    </div>

              <button
                      type="submit"
                      disabled={isLoading}
                className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold py-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Signing in...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </div>
                )}
              </button>
            </form>
          ) : (
            <div className="text-center text-slate-600 py-8">
              <p className="mb-4">Registration coming soon</p>
              <p className="text-sm">Please contact us to set up your account</p>
            </div>
          )}

          {/* Demo Credentials */}
          <div className="mt-8 p-4 bg-slate-50 rounded-xl">
            <h3 className="font-semibold text-slate-800 mb-3 text-center text-sm">Demo Credentials</h3>
            <div className="space-y-2 text-xs">
              {demoCredentials.map((cred, index) => (
                <div key={index} className="flex justify-between items-center p-2 bg-white rounded-lg">
                  <span className="font-medium text-slate-700">{cred.type}:</span>
                  <span className="font-mono text-slate-600">{cred.email}</span>
                  </div>
                ))}
              <div className="text-center text-slate-500 text-xs mt-2">
                Password: demo123, admin123, doctor123
              </div>
            </div>
          </div>

          {/* Back to Website */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-slate-500 hover:text-teal-600 transition-colors">
              ← Back to Website
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;